'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
    description?: string;
    image?: string;
}

interface PlanIntroSectionProps {
    plan: Plan;
    translationNamespace: string;
    imageSrc?: string;
    descriptionKeys?: string[];
}

export function PlanIntroSection({
    plan,
    translationNamespace,
    imageSrc,
    descriptionKeys = ['description1', 'description2']
}: PlanIntroSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    // Use provided imageSrc or fallback to plan.image
    const imageSource = imageSrc || plan.image || '';

    return (
        <SectionWrapper ref={sectionRef.ref} variant="gradient" padding="md" className="relative">
            <div className="container">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className={cn(
                        "order-2 lg:order-1 transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    )}>
                        <Typography variant="h2" className="mb-5">
                            {plan.name}
                        </Typography>
                        <div className="mb-8 space-y-4">
                            {descriptionKeys.map((key, index) => (
                                <Typography
                                    key={index}
                                    variant="p"
                                    className="leading-relaxed"
                                    style={{ transitionDelay: `${100 + index * 100}ms` }}
                                >
                                    {t(key)}
                                </Typography>
                            ))}
                        </div>
                    </div>
                    <div className={cn(
                        "order-1 lg:order-2 transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                    )} style={{ transitionDelay: '200ms' }}>
                        {imageSource && (
                            <div className="relative w-full flex items-center justify-center">
                                {imageSource.toLowerCase().endsWith('.gif') ? (
                                    <img
                                        src={imageSource}
                                        alt={plan.name}
                                        className="w-full h-auto max-h-[400px] object-contain"
                                        loading="eager"
                                    />
                                ) : (
                                    <div className="relative w-full aspect-video">
                                        <Image
                                            src={imageSource}
                                            alt={plan.name}
                                            fill
                                            className="object-contain"
                                            priority
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

